import React, { useState } from "react";
import BaseDropdown from "../../../components/ui/BaseDropdown";
import { Avatar } from "@mui/material";
import dayjs from "dayjs";
import { useNotificationsSocket } from "../../../hooks/useNotificationsSocket";
import useAuth from "../../../hooks/useAuth";
import { navigateWithReload, withCustomerBoardParam } from "../../../utils/helpers";
import { UserType } from "../../../utils/enum";

const Notifications = ({ isNotificationDropdownOpen, setIsNotificationDropdownOpen }) => {
  const { auth, authRole } = useAuth();
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    unreadNotificationCount,
    loadMoreOtherNotifications,
  } = useNotificationsSocket();
  const [loadingMore, setLoadingMore] = useState(false);

  const handleScroll = async (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40 && !loadingMore) {
      setLoadingMore(true);
      try {
        await loadMoreOtherNotifications();
      } catch (err) {
        console.error("Error loading more notifications:", err);
      } finally {
        setLoadingMore(false);
      }
    }
  };

  return (
    <>
      <BaseDropdown
        setOpen={setIsNotificationDropdownOpen}
        open={isNotificationDropdownOpen}
        className="w-[526px] top-[40px] shadow-none !-left-[360px] !px-0 !py-0 "
        style={{
          boxShadow: "0 1px 10px rgba(0, 0, 0, 0.2)",
        }}
      >
        <div className="max-h-[500px] overflow-auto relative rounded-lg" onScroll={handleScroll}>
          <div className="flex items-center justify-between border-b p-3 top-0 sticky bg-white z-10 ">
            <p className="text-base text-dark font-normal">Notifications</p>
            {unreadNotificationCount > 0 && (
              <p className="text-primary cursor-pointer" onClick={() => markAllAsRead.mutate()}>
                Mark all as read
              </p>
            )}
          </div>
          {notifications?.map((notification) => (
            <Notification
              key={notification?.id}
              type={notification.type}
              createdAt={dayjs(notification.createdAt).fromNow()}
              title={notification.title}
              content={notification.content}
              isRead={notification.isRead}
              profilePicture={notification?.sender?.profilePicture}
              onClick={() => {
                markAsRead.mutate(notification.id);
                setIsNotificationDropdownOpen(false);
                // A full reload rather than a client-side navigate: the user may already
                // be on the page the notification points at (or on the same route for
                // another work order, whose cached queries are keyed without an id), in
                // which case navigating alone leaves the change that was just announced
                // off the screen.
                navigateWithReload(
                  notification.type === "FREELANCE_COMPANY_INVITE"
                    ? `/user/settings/linkedCompanies/${auth?.user?.id}`
                    : withCustomerBoardParam(notification?.url, authRole === UserType.CUSTOMER),
                );
              }}
            />
          ))}
        </div>
      </BaseDropdown>
    </>
  );
};

export default Notifications;

const Notification = ({ type, createdAt, content, title, isRead, onClick, profilePicture }) => {
  // Some headings are worded by audience: customers know counter offers as proposals.
  const { authRole } = useAuth();
  const renderAvatar = (type) => {
    switch (type) {
      case "WORK_ORDER_PUBLISHED":
        return (
          <Avatar sx={{ bgcolor: "#FF5F002A" }}>
            <i className="fa-solid fa-clipboard-list text-secondary"></i>
          </Avatar>
        );
      case "WORK_ORDER_ASSIGNED":
        return (
          <Avatar sx={{ bgcolor: "#2561A92A" }}>
            <i className="fa-solid fa-briefcase text-primary !text-[#2561A9]"></i>
          </Avatar>
        );
      case "WORK_ORDER_UNASSIGNED":
        return (
          <Avatar sx={{ bgcolor: "#9C27B02A" }}>
            <i className="fa-solid fa-briefcase text-primary !text-[#9C27B0]"></i>
          </Avatar>
        );
      case "WORK_ORDER_UPDATED":
        return (
          <Avatar sx={{ bgcolor: "#FF5F002A" }}>
            <i className="fa-solid fa-clipboard-list text-secondary"></i>
          </Avatar>
        );

      case "WORK_ORDER_CONFIRMED":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_READY_TO_GO":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_CHECK_IN":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_CHECK_OUT":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_ON_THE_WAY":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_RUNNING_LATE":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_WORK_DONE":
        return <Avatar src={profilePicture}></Avatar>;
      case "WORK_ORDER_FEEDBACK":
        return <Avatar src={profilePicture}></Avatar>;
      case "REQUESTED":
        return <Avatar src={profilePicture}></Avatar>;

      case "COUNTER":
        return <Avatar src={profilePicture}></Avatar>;

      case "ROUTED":
        return (
          <Avatar sx={{ bgcolor: "#2196F32A" }}>
            <i className="fa-solid fa-briefcase text-primary !text-[#2196F3]"></i>
          </Avatar>
        );
      case "ACCEPT":
        return <Avatar src={profilePicture}></Avatar>;
      case "WITHDRAW":
        return <Avatar src={profilePicture}></Avatar>;
      case "FREELANCE_COMPANY_INVITE":
        return (
          <Avatar sx={{ bgcolor: "#2561A92A" }}>
            <i className="fa-solid fa-building text-primary !text-[#2561A9]"></i>
          </Avatar>
        );
      case "WORK_ORDER_CANCELLED":
        return (
          <Avatar sx={{ bgcolor: "#F443362A" }}>
            <i className="fa-solid fa-briefcase text-primary !text-[#F44336]"></i>
          </Avatar>
        );
      case "system":
        return (
          <Avatar sx={{ bgcolor: "#2561A92A" }}>
            <i className="fa-solid fa-cog text-primary"></i>
          </Avatar>
        );
      case "offer":
        return (
          <Avatar sx={{ bgcolor: "#2561A92A" }}>
            <i className="fa-solid fa-handshake text-primary"></i>
          </Avatar>
        );
      default:
        return (
          <Avatar sx={{ bgcolor: "#2561A92A" }}>
            <i className="fa-light fa-bell-ring text-primary"></i>
          </Avatar>
        );
    }
  };

  const getTitle = (type) => {
    switch (type) {
      case "WORK_ORDER_PUBLISHED":
        return "New Work Order Published:";
      case "WORK_ORDER_ASSIGNED":
        return "🎉 Congratulations!";
      case "WORK_ORDER_UNASSIGNED":
        return "Work Order Unassigned:";
      case "WORK_ORDER_CONFIRMED":
        return "Work Order Confirmed:";
      case "WORK_ORDER_READY_TO_GO":
        return "Ready to Go:";
      case "WORK_ORDER_CHECK_IN":
        return "Engineer Checked In:";
      case "WORK_ORDER_CHECK_OUT":
        return "Engineer Checked Out:";
      case "WORK_ORDER_ON_THE_WAY":
        return "Engineer On The Way:";
      case "WORK_ORDER_RUNNING_LATE":
        return "Engineer Running Late:";
      case "WORK_ORDER_WORK_DONE":
        return "Work Completed:";
      case "REQUESTED":
        return "Offer Requested:";
      case "COUNTER":
        // A customer gets this when a client sends them a proposal.
        return authRole === UserType.CUSTOMER ? "Proposal Received:" : "Counter Offer Received:";
      case "ROUTED":
        return "Work Order Routed:";
      case "WITHDRAW":
        return authRole === UserType.CUSTOMER ? "Proposal Withdrawn:" : "Offer Withdrawn:";
      case "ACCEPT":
        return "Work Order Routed:";
      case "FREELANCE_COMPANY_INVITE":
        return "Company Invitation:";
      case "system":
        return "System Notification:";
      case "offer":
        return "New Offer Sent:";
      default:
        return "Notification:";
    }
  };

  return (
    <div
      className={`flex px-4 py-4 h-[90px] border-b cursor-pointer ${!isRead && "bg-light"}`}
      onClick={onClick}
    >
      {renderAvatar(type)}

      <div className="ml-3 w-full overflow-hidden">
        <div className="flex w-full justify-between">
          <p className="font-normal text-dark">{title}</p>
          <p className="text-[10px] text-[#A5A5A5]">{createdAt}</p>
        </div>

        <span className="text-xs line-clamp-2" dangerouslySetInnerHTML={{ __html: content }}></span>
      </div>
    </div>
  );
};
